import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Facebook, Instagram, Share2, Check } from "lucide-react";
import { api } from "../lib/api";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-CA", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

const NewsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancel = false;
    api
      .get(`/news/${id}`)
      .then((r) => {
        if (!cancel) setItem(r.data);
      })
      .catch((e) => {
        if (!cancel) setError(e.response?.status || "error");
      });
    return () => {
      cancel = true;
    };
  }, [id]);

  // Update document title for better social previews / SEO once loaded.
  useEffect(() => {
    if (item?.title) {
      document.title = `${item.title} | Coupe CH`;
    }
    return () => {
      document.title = "Coupe CH | 2026";
    };
  }, [item]);

  const share = async () => {
    const url = window.location.href;
    const data = { title: item?.title, text: item?.excerpt || "", url };
    try {
      if (navigator.share) {
        await navigator.share(data);
        return;
      }
    } catch {
      /* user cancelled */
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt("Copiez le lien :", url);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="font-display text-4xl uppercase text-[#E60012]">Nouvelle introuvable</h1>
          <p className="mt-4 text-white/60">
            Cet article n'existe plus ou n'a pas encore été publié.
          </p>
          <button
            onClick={() => navigate("/nouvelles")}
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-[#E60012] font-condensed uppercase tracking-[0.2em] text-xs"
          >
            <ArrowLeft className="w-4 h-4" /> Toutes les nouvelles
          </button>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <div className="text-white/40 font-condensed uppercase tracking-[0.25em] text-xs">
          Chargement…
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen" data-testid="news-detail-page">
      <article className="max-w-3xl mx-auto px-6 lg:px-10 py-10 sm:py-14">
        <Link
          to="/nouvelles"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white font-condensed uppercase tracking-[0.2em] text-xs mb-8"
          data-testid="news-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Toutes les nouvelles
        </Link>

        <div className="text-xs font-condensed uppercase tracking-[0.25em] text-[#E60012] flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5" /> {fmtDate(item.created_at)}
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-tight"
        >
          {item.title}
        </motion.h1>

        {item.excerpt && (
          <p className="mt-4 text-lg sm:text-xl text-white/70 italic leading-relaxed">
            {item.excerpt}
          </p>
        )}

        {item.image_base64 && (
          <div className="mt-8 -mx-6 sm:mx-0">
            <img
              src={item.image_base64}
              alt={item.title}
              className="w-full max-h-[520px] object-cover"
            />
          </div>
        )}

        <div className="mt-8 text-white/85 leading-relaxed whitespace-pre-line text-base sm:text-lg">
          {item.body}
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={share}
            data-testid="news-share-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#E60012] hover:brightness-110 text-white font-condensed uppercase tracking-[0.15em] text-xs"
          >
            {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
            {copied ? "Lien copié" : "Partager"}
          </button>
          {item.facebook_url && (
            <a
              href={item.facebook_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#1877F2] hover:bg-[#1565d8] text-white font-condensed uppercase tracking-[0.15em] text-xs"
            >
              <Facebook className="w-4 h-4" /> Facebook
            </a>
          )}
          {item.instagram_url && (
            <a
              href={item.instagram_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#E1306C] to-[#F77737] text-white font-condensed uppercase tracking-[0.15em] text-xs"
            >
              <Instagram className="w-4 h-4" /> Instagram
            </a>
          )}
        </div>
      </article>
    </div>
  );
};

export default NewsDetail;
